import {
    pixelBase,
    PixelFormValues,
    pixelSnapchatSchema,
    TrackingInfos,
    TrackingType,
    TrackingTypePixel,
} from "@/app/stores/[id]/storefront/types/tracking";
import { withDefaultPixelEvents } from "@/app/stores/[id]/storefront/constants/tracking";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { pixelIdProblem } from "@/lib/tracking/pixel-id";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface PixelFormProps {
  trackingConfig: TrackingInfos<TrackingTypePixel>;
  type: TrackingTypePixel;
  defaultValues: PixelFormValues;
  isLoading: boolean;
  savePixelConfig: (data: PixelFormValues) => void;
}

function PixelForm({
  trackingConfig,
  type,
  defaultValues,
  isLoading,
  savePixelConfig,
}: PixelFormProps) {
  const { t } = useTranslation('storefront');
  const schema = type === TrackingType.SNAPCHAT ? pixelSnapchatSchema : pixelBase;

  const initialValues = useMemo(
    () => withDefaultPixelEvents(type, defaultValues),
    [type, defaultValues],
  );

  const form = useForm<PixelFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });
  const { formState: { errors }, watch, setValue } = form


  useEffect(() => {
    form.reset(initialValues);
  }, [initialValues]);

  const currentPixelId = (watch('pixelId') || '').trim();
  const shapeMismatch =
    currentPixelId.length > 0 &&
    !pixelIdProblem(currentPixelId) &&
    !!trackingConfig.pixelIdRegex &&
    !trackingConfig.pixelIdRegex.test(currentPixelId);

    return (
        <>
        <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="enabled" className="text-sm font-medium">
            {t('tracking.pixel.enable', { name: trackingConfig.name })}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('tracking.pixel.enableDescription')}
          </p>
        </div>
        <Switch
          id="enabled"
          checked={watch('enabled')}
          onCheckedChange={(checked) => {
            setValue('enabled', checked, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            savePixelConfig({ ...watch(), enabled: checked });
          }}
          disabled={isLoading}
        />
      </div>
      <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.pixel.pixelConfiguration')}</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pixelId">
                {t('tracking.pixel.pixelId')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="pixelId"
                placeholder={trackingConfig.pixelIdPlaceholder}
                disabled={isLoading}
                value={watch('pixelId') || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue('pixelId', value, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  // Only persist values that can't hurt the storefront
                  // (TAG-1871). An empty field is allowed through so a merchant
                  // can clear a pixel — the save pipeline strips empty entries —
                  // but a pasted <script> never reaches stepConfig.pixels.
                  const problem = pixelIdProblem(value);
                  if (!problem || problem === 'required') {
                    savePixelConfig({ ...watch(), pixelId: value });
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                {trackingConfig.pixelIdDescription}
              </p>
              {errors.pixelId && (
                <p className="text-xs text-destructive">{errors.pixelId.message}</p>
              )}
              {/* Soft, non-blocking provider-shape check. The per-provider
                  patterns describe the ID formats we know about today, so a
                  mismatch is worth flagging but must not lock a merchant out of
                  an unusual-but-working ID. */}
              {!errors.pixelId && shapeMismatch && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  {t('tracking.pixel.pixelIdShapeWarning', { format: trackingConfig.pixelIdDescription })}
                </p>
              )}
            </div>
          </div>
        </div>
        {trackingConfig.events && <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.pixel.eventTracking')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {trackingConfig.events.map((event) => {
              // event.key is already correctly typed based on the tracking type
              const eventKey = event.key;
              return (
                <div key={String(event.key)} className="flex items-center space-x-2">
                  <Checkbox
                    id={`event-${event.key}`}
                    checked={watch(`events.${eventKey}` as any)}
                    onCheckedChange={(checked) => {
                      setValue(`events.${eventKey}` as any, checked as boolean);
                      savePixelConfig({ ...watch(), events: { ...watch('events'), [eventKey]: checked } });
                    }}
                    disabled={isLoading}
                  />
                  <Label htmlFor={`event-${event.key}`} className="cursor-pointer font-normal">
                    {event.label}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>}
      </>
    );
}

export default PixelForm;